<template>
    <h4 class="link-header mb-md" id="similar">Similar Patents</h4>
    <div class="patent-similarity display-flex w-100 mb-md flex-wrap">
        <div class="run-sim-button flex-grow-1">
            <div class="card-content h-100 w-100">
                <div
                    v-if="!loadingSimilarity && patentList.length === 0 && !noPatents"
                    class="cta h-100 display-flex align-items-center justify-center flex-wrap"
                >
                    <p class="large semi-bold w-100 text-center">
                        How does this patent compare to its most similar counterparts?
                    </p>
                    <AonButton
                        class="mt-n8"
                        label="Run Similarity"
                        @clicked="onFindSimilarPatents"
                    />
                </div>
                <div
                    v-if="!loadingSimilarity && noPatents"
                    class="no-patents h-100 display-flex align-items-center justify-center flex-wrap"
                >
                    <p class="large semi-bold w-100 text-center">No similar patents identified</p>
                </div>
                <AonCoverLoading :loading="loadingSimilarity" title="Finding Similar Patents" />
            </div>
        </div>
        <ArmoryOptionToggle
            v-if="(patentList.length > 0 || noPatents) && currentEntityPk"
            class="move"
            v-model="toggleValue"
            :options="[
                { label: 'All Patents', value: 'all' },
                { label: 'Company Specific Patents', value: 'entity' },
            ]"
            :disabled="loadingSimilarity"
            size="sm"
            @update:modelValue="handleToggleChange"
        />
        <AonCard v-if="patentList.length > 1" class="w-100 mb-sm">
            <div class="chart-holder">
                <p class="large semi-bold w-100">Timeline</p>
                <div id="similarPatentsChart"></div>
            </div>
            <div class="legend display-flex align-items-center">
                <div class="average display-flex align-items-center mr-xl">
                    <div class="indicator mr-sm"></div>
                    <p class="small">Avg. Filing Date</p>
                </div>
                <div class="similar display-flex align-items-center mr-xl">
                    <div class="indicator mr-sm"></div>
                    <p class="small">Similar Patents</p>
                </div>
                <div class="target display-flex align-items-center mr-md">
                    <div class="indicator mr-sm"></div>
                    <p class="small">This Patent</p>
                </div>
            </div>
        </AonCard>
        <div v-if="patentList.length > 0" class="patent-list w-100">
            <div
                v-for="patent in patentList"
                :key="patent.id"
                class="patent-item p-md my-xxs"
                @click="goToGooglePatents(patent.googlePatentsLink)"
            >
                <p class="link patent-link">{{ patent.patentId }}</p>
                <p class="bold text-moat-900 w-75 text-truncate">
                    {{ patent.title }}
                </p>
                <div class="info-container mt-md">
                    <div class="assignee display-flex my-sm" ref="assigneeRef">
                        <div class="w-100 display-flex align-items-center">
                            <font-awesome-icon
                                :icon="`fas fa-building`"
                                class="text-stone-600 mr-sm mt-n1"
                                v-tooltip="{
                                    content: 'Current Assignee',
                                    placement: 'left',
                                    delay: { show: 500, hide: 0 },
                                }"
                            />
                            <p class="no-wrap text-truncate semi-bold text-stone-600">
                                {{ patent?.entities ? patent?.entities : 'N/A' }}
                            </p>
                            <div class="divide"></div>
                            <p class="no-wrap text-truncate semi-bold text-stone-600">
                                <span>Ultimate Parent: </span
                                >{{ patent?.ultimateEntities ? patent?.ultimateEntities : 'N/A' }}
                            </p>
                        </div>
                    </div>
                    <div class="dates" ref="datesRef">
                        <div class="w-100 display-flex align-items-center">
                            <font-awesome-icon
                                :icon="`fas fa-calendar`"
                                class="text-stone-600 mr-sm mt-n1"
                                v-tooltip="{
                                    content: 'Filing Date',
                                    placement: 'left',
                                    delay: { show: 500, hide: 0 },
                                }"
                            />
                            <p class="no-wrap text-truncate text-stone-600">
                                <span>Filed </span>{{ filters.toUTCString(patent?.filingDate) }}
                            </p>
                            <div class="divide"></div>
                            <p class="no-wrap text-truncate text-stone-600">
                                <span>Publication: </span
                                >{{ filters.toUTCString(patent?.publicationDate) }}
                            </p>
                            <div class="divide"></div>
                            <p class="no-wrap text-truncate text-stone-600">
                                <span>Priority: </span
                                >{{ filters.toUTCString(patent?.priorityDate) }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {
    ref,
    inject,
    watch,
    nextTick,
    onMounted,
    onBeforeUnmount,
    defineExpose,
    computed,
} from 'vue'
import { getSimilarPatents } from '@/api/patentViewer'
import { useEntityStore, usePatentViewerStore } from '@/stores'

import * as am5 from '@amcharts/amcharts5'
import { useSimpleRangeBulletChart } from '@/composables/charts/simpleRangeBulletChart'
import { config } from '@/config'
import { debounce } from 'lodash-es'

const logger = inject('logger')
const filters = inject('filters')
const eventBus = inject('eventBus')

const entityStore = useEntityStore()
const patentViewerStore = usePatentViewerStore()

const props = defineProps({
    patentDetails: {
        type: Object,
        default: () => ({}),
    },
    loadingPatentDetail: {
        type: Boolean,
        default: false,
    },
})

const loadingSimilarity = ref(false)
const patentList = ref([])
const noPatents = ref(false)
const chartData = ref(null)
const currentPatentId = ref(null)
const toggleValue = ref('entity')
const toggleStateMap = ref({})

let root
let simpleRangeBulletChart

const debouncedFetchSimilarPatents = debounce(async (patentId, entityPk) => {
    await fetchSimilarPatents(patentId, entityPk)
}, 300)

onMounted(() => {
    eventBus.on('patent-changed', handlePatentChanged)

    if (props.patentDetails?.publicationPk) {
        checkCacheAndLoadData(props.patentDetails.publicationPk)
    }
})

onBeforeUnmount(() => {
    eventBus.off('patent-changed', handlePatentChanged)

    if (root) {
        root.dispose()
        root = null
    }

    debouncedFetchSimilarPatents.cancel()
})

const handlePatentChanged = (newPatentId) => {
    resetComponentState()

    if (newPatentId) {
        // Restore toggle state
        if (toggleStateMap.value[newPatentId]) {
            toggleValue.value = toggleStateMap.value[newPatentId]
        } else {
            toggleValue.value = 'entity'
        }

        checkCacheAndLoadData(newPatentId)
    }
}

const resetComponentState = () => {
    patentList.value = []
    noPatents.value = false
    chartData.value = null

    if (root) {
        root.dispose()
        root = null
    }
}

const currentEntityPk = computed(() => {
    if (patentViewerStore.entityPkList[0]) {
        return patentViewerStore.entityPkList[0]
    } else if (entityStore.entity?.aon_entity_pk) {
        return entityStore.entity.aon_entity_pk
    }
    return null
})

watch(
    () => props.patentDetails,
    (newVal, oldVal) => {
        if (newVal?.publicationPk && newVal.publicationPk !== currentPatentId.value) {
            currentPatentId.value = newVal.publicationPk

            resetComponentState()

            // Restore toggle state
            if (toggleStateMap.value[newVal.publicationPk]) {
                toggleValue.value = toggleStateMap.value[newVal.publicationPk]
            } else {
                toggleValue.value = 'entity'
            }
            checkCacheAndLoadData(newVal.publicationPk)
        }
    },
    { immediate: true }
)

const checkCacheAndLoadData = (patentId) => {
    if (!patentId) return

    // Check if we have cached data
    if (patentViewerStore.similarPatentsExistInCache(patentId)) {
        const cachedData = patentViewerStore.getCachedSimilarPatents(patentId)
        patentList.value = cachedData

        if (patentList.value.length === 0) {
            noPatents.value = true
        } else {
            nextTick(() => {
                mapDataToChart()
            })
        }
    }
}

const onFindSimilarPatents = async (ignore = false) => {
    if (ignore) {
        debouncedFetchSimilarPatents(props.patentDetails.publicationPk, null)
    } else {
        if (props.patentDetails?.publicationPk) {
            debouncedFetchSimilarPatents(props.patentDetails.publicationPk, currentEntityPk.value)
        }
    }
}

const fetchSimilarPatents = async (patentId, entityPk) => {
    // If we already have cached data, use it
    if (patentViewerStore.similarPatentsExistInCache(patentId)) {
        const cachedData = patentViewerStore.getCachedSimilarPatents(patentId)
        patentList.value = cachedData

        if (patentList.value.length === 0) {
            noPatents.value = true
        } else {
            mapDataToChart()
        }
        return
    }

    loadingSimilarity.value = true
    try {
        const { data } = await getSimilarPatents(patentId, entityPk)
        patentList.value = data

        // Cache the results
        patentViewerStore.setCachedSimilarPatents(patentId, data)

        if (patentList.value.length === 0) {
            noPatents.value = true
            return
        }
    } catch (err) {
        logger.error(err)
        eventBus?.emit?.('snacktime', {
            type: 'error',
            message: 'Failed to fetch similar patents',
            contextual: true,
        })
        noPatents.value = true
    } finally {
        if (patentList.value.length > 1) {
            mapDataToChart()
        }
        loadingSimilarity.value = false
    }
}

const mapDataToChart = () => {
    if (patentList.value.length <= 0) return

    const thisPatentFilingDate = new Date(props.patentDetails?.filingDate).getTime()

    // Earliest filing date
    const earliestFilingDate = patentList.value.reduce((earliest, patent) => {
        const currentFilingDate = new Date(patent.filingDate).getTime()
        return currentFilingDate < earliest ? currentFilingDate : earliest
    }, thisPatentFilingDate) // Start with this patent's filing date as the initial value

    // Latest filing date
    const latestFilingDate = patentList.value.reduce((latest, patent) => {
        const currentFilingDate = new Date(patent.filingDate).getTime()
        return currentFilingDate > latest ? currentFilingDate : latest
    }, thisPatentFilingDate) // Start with this patent's filing date as the initial value

    // Average
    const averageFilingDate =
        (patentList.value.reduce((sum, patent) => {
            return sum + new Date(patent.filingDate).getTime()
        }, 0) +
            thisPatentFilingDate) /
        (patentList.value.length + 1)

    // All patent dates (excluding earliest, latest, and this patent's filing date)
    const allPatentDates = patentList.value
        .filter((patent) => {
            const filingDate = new Date(patent.filingDate).getTime()
            return (
                filingDate !== earliestFilingDate &&
                filingDate !== latestFilingDate &&
                patent.patentId !== props.patentDetails?.patentId
            )
        })
        .map((patent) => {
            return {
                date: new Date(patent.filingDate).getTime(),
                id: patent.patentId,
            }
        })

    chartData.value = [
        {
            name: 'Similar Patents Timeline',
            entry: earliestFilingDate,
            latest: latestFilingDate,
            target: thisPatentFilingDate,
            average: averageFilingDate,
            patentDates: allPatentDates,
        },
    ]

    nextTick(() => {
        initializeChart()
    })
}

const initializeChart = async () => {
    if (root) {
        root.dispose()
        root = null
    }

    await nextTick()

    // Only initialize chart if the component is still mounted and we have data
    if (!chartData.value || chartData.value.length === 0) return

    try {
        am5.addLicense(config.license.AMChartsLicense)
        root = am5.Root.new('similarPatentsChart')

        simpleRangeBulletChart = useSimpleRangeBulletChart(root)

        simpleRangeBulletChart.configureSimpleRangeBullet()
        simpleRangeBulletChart.generateAxes(chartData.value)
        simpleRangeBulletChart.generateSeries(chartData.value)
    } catch (err) {
        logger.error('Error initializing chart:', err)
    }
}

const goToGooglePatents = (googleLink) => {
    if (!googleLink) return
    window.open(`${googleLink}`, '_blank')
}

const handleToggleChange = (value) => {
    // Save the toggle state
    if (props.patentDetails?.publicationPk) {
        toggleStateMap.value[props.patentDetails.publicationPk] = value
    }

    // Always clear cache for the current patent when toggling
    if (props.patentDetails?.publicationPk) {
        patentViewerStore.removeCachedSimilarPatents(props.patentDetails.publicationPk)
    }

    if (value === 'all') {
        onFindSimilarPatents(true)
    } else {
        onFindSimilarPatents()
    }
}

defineExpose({
    onFindSimilarPatents,
    patentList,
    loadingSimilarity,
    noPatents,
})
</script>

<style scoped lang="scss">
@forward '@/styles/new_styles.scss';

.patent-similarity {
    min-height: 200px;

    position: relative;

    .patent-item {
        background: var(--color-stone-200);
        border: solid 2px transparent;
        cursor: pointer;

        position: relative;

        transition: all 0.2s ease-in-out;

        &:hover {
            background: var(--color-moat-50);
            border-color: var(--color-moat-600);
        }

        &:first-of-type {
            border-radius: 8px 8px 0 0;
        }

        &:last-of-type {
            border-radius: 0 0 8px 8px;
        }
    }

    .patent-link {
        position: absolute;
        top: 20px;
        right: 20px;
    }

    .divide {
        border-radius: 50%;
        width: 5px;
        height: 5px;
        background-color: var(--color-stone-200);
        margin: 0 12px;
    }

    #similarPatentsChart {
        height: 100%;
        height: 130px;

        div {
            height: 100%;
        }
    }

    .legend {
        position: absolute;
        top: 20px;
        right: 20px;

        .average {
            .indicator {
                background-color: var(--color-moat-600);
                width: 4px;
                height: 20px;
            }
        }

        .similar,
        .target {
            .indicator {
                background-color: var(--color-moat-900);
                width: 7px;
                height: 7px;
                border-radius: 50%;
            }
        }

        .target {
            .indicator {
                background-color: var(--color-gold);
                width: 14px;
                height: 14px;
            }
        }
    }

    .move {
        position: absolute;
        top: -50px;
        right: 0;
    }
}
</style>
